import { useState } from 'react'
import { ClipboardList, Mail, Ship, SquarePen } from 'lucide-react'
import type { AgentThread } from '@/lib/api/agent'
import { FreshAttachments, voiceSupported } from './AssistantThread'
import { Composer } from './Composer'
import { RecentConversations } from './ConversationHistory'
import type { MentionItem } from './MentionCombobox'
import type { AssistantController } from './useAssistant'
import { VoiceStage } from './VoicePanel'

/**
 * Starting points drawn from what Beacon can do with its tools: read inquiries,
 * draft client replies, track a consignment, open an inquiry. Each fills the
 * box to be finished; none sends on its own. Only admins may draft client
 * replies, so only they are offered that one.
 */
const SUGGESTIONS = [
  {
    icon: ClipboardList,
    label: 'Summarise my inquiries',
    text: 'Summarise my open inquiries and what each one is waiting on.',
  },
  { icon: Mail, label: 'Draft a client reply', text: 'Draft a reply to ', adminOnly: true },
  { icon: Ship, label: 'Track a consignment', text: 'Where is consignment ' },
  { icon: SquarePen, label: 'New inquiry', text: 'Start a sourcing inquiry for ' },
] as const

/**
 * The assistant's home: one question and one box in the middle of the page,
 * a few places to start, and the last conversations to pick back up. Talking
 * turns the middle into the call itself. The first message moves into the
 * conversation, which keeps it.
 */
export function AssistantHome({
  assistant,
  mentionItems,
  firstName,
  isAdmin,
  threads,
  onOpenThread,
  onViewAll,
}: {
  assistant: AssistantController
  mentionItems: MentionItem[]
  firstName: string | null
  isAdmin: boolean
  threads: AgentThread[]
  onOpenThread: (threadId: string) => void
  onViewAll: () => void
}) {
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null)
  const { voice } = assistant
  const inCall = voice.status !== 'idle'

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center gap-8 px-1 pt-6 pb-[10vh]">
        {inCall ? (
          <>
            <h1 className="sr-only">Beacon</h1>
            <VoiceStage
              status={voice.status}
              lines={voice.lines}
              muted={voice.muted}
              error={voice.error}
              onToggleMute={voice.toggleMute}
              onEnd={voice.active ? voice.stop : voice.dismiss}
            />
          </>
        ) : (
          <>
            <div className="space-y-2 text-center">
              <p className="eyebrow">Beacon</p>
              <h1 className="text-3xl md:text-4xl">
                What can I help with{firstName ? `, ${firstName}` : ''}?
              </h1>
            </div>

            <div className="space-y-3">
              <Composer
                variant="card"
                rows={3}
                autoFocus
                prefill={prefill}
                placeholder="Ask about an inquiry, a consignment or a client. Use @ to point at one."
                onSend={(content) => void assistant.send(content)}
                onStop={assistant.stop}
                streaming={assistant.streaming}
                mentionItems={mentionItems}
                uploading={assistant.uploading}
                onUpload={assistant.uploadFiles}
                attachedCount={assistant.fresh.length}
                onVoice={voiceSupported ? assistant.startVoice : undefined}
                voiceActive={voice.active}
                above={<FreshAttachments assistant={assistant} className="px-3 pt-3" />}
              />
              {assistant.error && (
                <p className="text-center text-sm text-destructive">{assistant.error}</p>
              )}
              <ul aria-label="Suggestions" className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.filter((suggestion) => isAdmin || !('adminOnly' in suggestion)).map(
                  (suggestion) => (
                    <li key={suggestion.label}>
                      <button
                        type="button"
                        onClick={() =>
                          setPrefill((current) => ({
                            text: suggestion.text,
                            nonce: (current?.nonce ?? 0) + 1,
                          }))
                        }
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground"
                      >
                        <suggestion.icon aria-hidden className="size-3.5" />
                        {suggestion.label}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </>
        )}

        {!inCall && (
          <RecentConversations threads={threads} onSelect={onOpenThread} onViewAll={onViewAll} />
        )}
      </div>
    </div>
  )
}
